import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getPlatformApiUrl } from '@/lib/superadmin-api';

const SUPERADMIN_TOKEN_KEY = 'superadmin.token';
const SUPERADMIN_USER_KEY = 'superadmin.user';

export interface SuperadminUser {
  id: string;
  email: string;
  name: string;
  isSuperAdmin: true;
  emailVerified?: boolean;
}

interface SuperadminAuthContextValue {
  user: SuperadminUser | null;
  token: string | null;
  isReady: boolean;
  isAuthenticated: boolean;
  login: (token: string, user: SuperadminUser) => void;
  logout: () => Promise<void>;
  getAuthHeader: () => Record<string, string>;
}

const SuperadminAuthContext = createContext<SuperadminAuthContextValue | undefined>(undefined);

function readStoredUser(): SuperadminUser | null {
  try {
    const saved = localStorage.getItem(SUPERADMIN_USER_KEY);
    return saved ? (JSON.parse(saved) as SuperadminUser) : null;
  } catch {
    return null;
  }
}

export function SuperadminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SuperadminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setToken(localStorage.getItem(SUPERADMIN_TOKEN_KEY));
    setUser(readStoredUser());
    setIsReady(true);
  }, []);

  const value = useMemo<SuperadminAuthContextValue>(() => ({
    user,
    token,
    isReady,
    isAuthenticated: !!token && !!user,
    login: (nextToken, nextUser) => {
      setToken(nextToken);
      setUser(nextUser);
      localStorage.setItem(SUPERADMIN_TOKEN_KEY, nextToken);
      localStorage.setItem(SUPERADMIN_USER_KEY, JSON.stringify(nextUser));
    },
    logout: async () => {
      if (token) {
        try {
          await fetch(getPlatformApiUrl('/auth/logout'), {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch {
          // ignore logout transport failures; local state still must clear
        }
      }

      setToken(null);
      setUser(null);
      localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
      localStorage.removeItem(SUPERADMIN_USER_KEY);
      window.location.assign('/superadmin/login');
    },
    getAuthHeader: () => {
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      return headers;
    },
  }), [isReady, token, user]);

  return <SuperadminAuthContext.Provider value={value}>{children}</SuperadminAuthContext.Provider>;
}

export function useSuperadminAuth() {
  const context = useContext(SuperadminAuthContext);
  if (!context) {
    throw new Error('useSuperadminAuth must be used within SuperadminAuthProvider');
  }

  return context;
}
